/**
 * Event tab — admin CRUD for the MVP rally-event row.
 *
 * Per design-system/ui_kits/mvp/admin.html:230-278. Two-column grid
 * (1.3fr / 1fr): Details form on the left, three stacked cards on
 * the right (Status, Public viewer share, Admin team).
 *
 * Above the wireframe layout: a small event picker + "New event"
 * affordance. The wireframe assumes a single active event; in real
 * use an organizer maintains several across a season, so the picker
 * lets them switch. When zero events exist, the page renders an
 * empty state with a "Create your first event" CTA.
 *
 * Backend integration via services/fleet.ts (calls live endpoints).
 * The Save button uses PUT /api/fleet/events/<id>; the QR poster
 * button opens the QrPoster dialog.
 *
 * Out of scope:
 *   - Hero image upload (gap-analysis deferred)
 *   - Social card button (stubbed disabled — copy is enough for MVP)
 *   - Multi-admin invite flow (Admin team card is informational)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NEW_EVENT_PARAM } from '@/components/Navbar';
import {
  Copy,
  FileText,
  Link as LinkIcon,
  Plus,
  QrCode as QrCodeIcon,
  Route,
  Save,
  Share2,
  Trash2,
  Upload,
  UserPlus,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import QrPoster from '@/components/QrPoster';
import { RecordingSettingsCard } from '@/components/RecordingSettingsCard';
import { UploadPolicyCard } from '@/components/UploadPolicyCard';
import V2UploadPolicyCard from '@/components/V2UploadPolicyCard';
import {
  EventRow,
  FleetApiError,
  createEvent,
  deleteRaceline,
  publicViewerEventUrl,
  publicViewerUrl,
  updateEvent,
  uploadRaceline,
} from '@/services/fleet';
import UnendedSessionsCard from './UnendedSessionsCard';
import {
  emptyForm,
  FormState,
  eventToForm,
  formToCreateBody,
  formToUpdatePatch,
  timezoneChangePreview,
  windowError,
} from './eventForm';
import { useCurrentEvent } from '@/state/currentEvent';

// 'Circuit' switches the Stages tab to the circuit editor (closed-loop
// draw + lap line) and enables lap counting on the public viewer.
export const EVENT_TYPES = ['Rally', 'Track day', 'Hillclimb', 'Autocross', 'Practice', 'Circuit'] as const;
const COMMON_TIMEZONES = [
  'Europe/Tallinn',
  'Europe/Helsinki',
  'Europe/Berlin',
  'Europe/London',
  'America/New_York',
  'UTC',
] as const;

export const EventTab: React.FC = () => {
  // Events list + selection now live in the global CurrentEventProvider
  // so the Navbar pill, EventTab, StagesTab, RosterTab all subscribe
  // to a single source of truth. Per-tab selector dropdowns retired.
  const { events, currentEvent, setCurrentEventId, refresh, loading } =
    useCurrentEvent();
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(() => emptyForm());
  // The form as last loaded from the server; Save sends only what differs from it.
  const loadedFormRef = useRef<FormState>(emptyForm());
  const [dirty, setDirty] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newForm, setNewForm] = useState<FormState>(() => emptyForm());

  // Arriving from the Navbar's "New event" means create, not edit. Consume the flag on
  // arrival so a later reload or back-navigation doesn't reopen the dialog over an event
  // the operator has since chosen to edit.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get(NEW_EVENT_PARAM) !== '1') return;
    setNewForm(emptyForm());
    setError(null);
    setNewEventOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete(NEW_EVENT_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Re-hydrate strategy:
  //   1. Selection actually swapped (Navbar pill, post-Save, post-create)
  //      → reset form + clear dirty. Switching events implicitly discards
  //        unsaved edits — explicit prompt-to-discard is post-MVP.
  //   2. Same event, background refresh (15 s poll from CurrentEventProvider)
  //      → only update form if the user hasn't typed anything. Without the
  //        `dirty` guard, the poll silently overwrote in-progress edits
  //        (user-reported: "heartbeat resets entries if you don't save
  //        quickly").
  const loadForm = useCallback((next: FormState) => {
    loadedFormRef.current = next;
    setForm(next);
  }, []);

  const lastEventIdRef = useRef<number | null>(null);
  useEffect(() => {
    const newId = currentEvent?.id ?? null;
    const selectionChanged = newId !== lastEventIdRef.current;
    lastEventIdRef.current = newId;
    if (selectionChanged) {
      if (currentEvent) {
        loadForm(eventToForm(currentEvent));
      } else if (events.length === 0) {
        loadForm(emptyForm());
      }
      setDirty(false);
      setError(null);
    } else if (!dirty && currentEvent) {
      loadForm(eventToForm(currentEvent));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent, events.length]);

  // Field handlers (DetailsCard / EmptyState) go through this so any
  // user typing flips dirty=true and the poll-driven rehydrate skips.
  const setFormDirty = useCallback(
    (next: FormState | ((prev: FormState) => FormState)) => {
      setForm(next);
      setDirty(true);
    },
    [],
  );

  const selected = currentEvent;

  const handleNewEventSave = async () => {
    setCreating(true);
    setError(null);
    try {
      const body = formToCreateBody(newForm);
      if (!body.name) {
        setError('Event name is required');
        setCreating(false);
        return;
      }
      const windowErr = windowError(newForm);
      if (windowErr) {
        setError(windowErr);
        setCreating(false);
        return;
      }
      const created = await createEvent(body);
      await refresh();
      setCurrentEventId(created.id);
      loadForm(eventToForm(created));
      setDirty(false);
      setNewEventOpen(false);
      setNewForm(emptyForm());
    } catch (err) {
      setError(formatError(err));
    } finally {
      setCreating(false);
    }
  };

  const openNewEventModal = () => {
    setNewForm(emptyForm());
    setError(null);
    setNewEventOpen(true);
  };

  const handleSave = async () => {
    if (!selected) {
      // Empty state: form is a create form, Save = POST.
      setSaving(true);
      setError(null);
      try {
        const body = formToCreateBody(form);
        if (!body.name) {
          setError('Event name is required');
          setSaving(false);
          return;
        }
        const windowErr = windowError(form);
        if (windowErr) {
          setError(windowErr);
          setSaving(false);
          return;
        }
        const created = await createEvent(body);
        await refresh();
        setCurrentEventId(created.id);
        loadForm(eventToForm(created));
        setDirty(false);
      } catch (err) {
        setError(formatError(err));
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (!form.name.trim()) {
        setError('Event name is required');
        setSaving(false);
        return;
      }
      const patch = formToUpdatePatch(loadedFormRef.current, form);
      // Legacy events can have end < start; only block saves that change the window.
      if ('active_from' in patch || 'active_until' in patch) {
        const windowErr = windowError(form);
        if (windowErr) {
          setError(windowErr);
          setSaving(false);
          return;
        }
      }
      if (Object.keys(patch).length > 0) {
        const updated = await updateEvent(selected.id, patch);
        await refresh();
        loadForm(eventToForm(updated));
      }
      setDirty(false);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = async (url: string) => {
    if (!url || url === '—') return;
    try {
      await navigator.clipboard.writeText(url);
      setCopyToast(true);
      window.setTimeout(() => setCopyToast(false), 1500);
    } catch {
      // ignore — clipboard access denied
    }
  };

  if (loading) {
    return (
      <div className="mx-auto mt-12 flex max-w-5xl items-center justify-center px-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto mb-8 mt-4 max-w-6xl px-2 sm:px-6">
      {/* Page header */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <FileText className="h-7 w-7 text-accent" />
          <div>
            <h1 className="text-2xl font-semibold leading-tight">Event</h1>
            <p className="text-sm text-fg-secondary">
              {selected
                ? `Details and sharing for ${selected.name}`
                : 'Create an event to share a join code with phones and viewers.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Per-tab event selector retired. Pick events via the
              global pill in the Navbar (admin.html:204 design). */}
          <Button
            variant="ghost"
            size="sm"
            onClick={openNewEventModal}
            disabled={creating}
            className="gap-1"
          >
            <Plus className="size-4" />
            New event
          </Button>
          <Button onClick={handleSave} disabled={saving || !dirty} className="gap-1">
            <Save className="size-4" />
            {saving ? 'Saving…' : 'Save changes'}
            {/* Dirty dot — the form already tracks dirty (poll-overwrite
                guard); surface it so unsaved edits are visible. */}
            {dirty && !saving && (
              <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-fg-inverse/80" />
            )}
          </Button>
          {dirty && !saving && (
            <span className="text-xs text-status-warning">Unsaved changes</span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
          {error}
        </div>
      )}

      <UnendedSessionsCard
        key={selected?.id ?? 'no-event'}
        eventId={selected?.id ?? null}
      />

      {events.length === 0 && !selected ? (
        <EmptyState form={form} setForm={setFormDirty} />
      ) : (
        <div className="grid gap-3 lg:grid-cols-[1.3fr_1fr]">
          {/* Left — Details */}
          <DetailsCard
            form={form}
            setForm={setFormDirty}
            timezonePreview={selected ? timezoneChangePreview(loadedFormRef.current, form) : null}
          />

          {/* Right — Status / Share / Admin team / Recording settings / Upload policy */}
          <div className="flex flex-col gap-3">
            <StatusCard event={selected} />
            <ShareCard
              event={selected}
              onOpenQr={() => setQrOpen(true)}
              onCopy={handleCopyUrl}
              copyToast={copyToast}
            />
            <PublicViewerCard event={selected} onCopy={handleCopyUrl} />
            <RacelineCard event={selected} onUpdated={refresh} />
            <AdminTeamCard />
            <UploadPolicyCard eventId={selected?.id ?? null} />
            {/* key-remount per selection (S9 review R3): the card's whole state dies
                synchronously with the selection, so no render window can paint one
                event's authority under another's identity. */}
            <V2UploadPolicyCard key={selected?.id ?? 'none'} eventId={selected?.id ?? null} />
            <RecordingSettingsCard />
          </div>
        </div>
      )}

      <QrPoster
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        eventName={selected?.name || form.name || 'Event'}
        joinCode={selected?.join_code ?? null}
      />

      <Dialog
        open={newEventOpen}
        onOpenChange={(o) => {
          if (!o) {
            setNewEventOpen(false);
            setError(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-accent">
              <Plus className="size-5" />
              New event
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-fg-secondary">
            Fill in the basics — you can edit the rest after the event is created.
          </p>
          <DetailsCard form={newForm} setForm={setNewForm} />
          <DialogFooter className="gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => {
                setNewEventOpen(false);
                setError(null);
              }}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button onClick={handleNewEventSave} disabled={creating} className="gap-1">
              <Save className="size-4" />
              Create event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventTab;

// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ form: FormState; setForm: React.Dispatch<React.SetStateAction<FormState>> }> = ({
  form,
  setForm,
}) => {
  return (
    <div className="max-w-2xl">
      <DetailsCard form={form} setForm={setForm} />
      <p className="mt-3 text-xs text-fg-secondary">
        Fill the form and click <span className="font-semibold">Save changes</span> to mint
        a 6-character join code + QR poster.
      </p>
    </div>
  );
};

const DetailsCard: React.FC<{
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  timezonePreview?: string | null;
}> = ({ form, setForm, timezonePreview }) => {
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm({ ...form, [k]: v });
  return (
    <Card className="p-4 sm:p-5">
      <CardContent className="space-y-4 p-0">
        <SectionLabel>Details</SectionLabel>

        <Field label="Event name">
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} required />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <Select value={form.type} onValueChange={(v) => set('type', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* A stored type outside EVENT_TYPES (e.g. "Stage",
                    set via the API) rendered the trigger BLANK and a
                    save would silently rewrite it — keep the current
                    value selectable. */}
                {form.type && !EVENT_TYPES.includes(form.type as (typeof EVENT_TYPES)[number]) && (
                  <SelectItem value={form.type}>{form.type}</SelectItem>
                )}
                {EVENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Starts">
            <div className="flex gap-2">
              <Input
                type="date"
                value={form.startDate}
                onChange={(e) => set('startDate', e.target.value)}
              />
              <Input
                type="time"
                value={form.startTime}
                className="w-28 shrink-0"
                onChange={(e) => set('startTime', e.target.value)}
              />
            </div>
          </Field>
        </div>

        <Field label="Ends">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="date"
              value={form.endDate}
              disabled={form.openEnded}
              className="max-w-[180px]"
              onChange={(e) => set('endDate', e.target.value)}
            />
            <Input
              type="time"
              value={form.endTime}
              disabled={form.openEnded}
              className="w-28 shrink-0"
              onChange={(e) => set('endTime', e.target.value)}
            />
            <label className="flex cursor-pointer items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={form.openEnded}
                onChange={(e) => set('openEnded', e.target.checked)}
                className="size-4 cursor-pointer"
              />
              <span>Open-ended</span>
            </label>
          </div>
          <p className="mt-1 text-xs text-fg-secondary">
            Multi-day windows are fine. Open-ended events stay live (phones keep
            arming, laps keep counting) until you set an end.
          </p>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Location">
            <Input
              value={form.location}
              placeholder="Tartu, Estonia"
              onChange={(e) => set('location', e.target.value)}
            />
          </Field>
          <Field label="Time zone">
            <Select value={form.timezone} onValueChange={(v) => set('timezone', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMMON_TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {timezonePreview && (
              <p className="mt-1 text-xs text-fg-secondary">{timezonePreview}</p>
            )}
          </Field>
        </div>

        <Field label="Description">
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>

        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.public}
            onChange={(e) => set('public', e.target.checked)}
            className="size-4 cursor-pointer"
          />
          <span>Public viewer access (un-tick for an invite-only event)</span>
        </label>

        {/* The directory row hands out the join code, so
            listing is a deliberate, separate opt-in. */}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.listed}
            onChange={(e) => set('listed', e.target.checked)}
            className="size-4 cursor-pointer"
          />
          <span>List in the app&apos;s public event directory (publishes the join code)</span>
        </label>

        {/* Self-service flags — what an individual driver may control
            from the phone app (defaults ON; the app enforces them). */}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.allowIndividualSchedule}
            onChange={(e) => set('allowIndividualSchedule', e.target.checked)}
            className="size-4 cursor-pointer"
          />
          <span>Drivers may set their own flag / start time from the app</span>
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.allowIndividualQuality}
            onChange={(e) => set('allowIndividualQuality', e.target.checked)}
            className="size-4 cursor-pointer"
          />
          <span>Drivers may change recording quality from the app</span>
        </label>
      </CardContent>
    </Card>
  );
};

const StatusCard: React.FC<{ event: EventRow | null }> = ({ event }) => {
  if (!event) return null;
  const now = Date.now();
  const from = Date.parse(event.active_from);
  // Open-ended: no end — never reads 'ended'.
  const until = event.active_until ? Date.parse(event.active_until) : Infinity;
  let state: { label: string; color: string };
  if (now < from) state = { label: 'scheduled', color: 'text-fg-secondary' };
  else if (now > until) state = { label: 'ended', color: 'text-fg-secondary' };
  else
    state = {
      label: event.active_until ? '● live now' : '● live now (open-ended)',
      color: 'text-status-error',
    };

  return (
    <Card className="p-4">
      <CardContent className="space-y-2 p-0">
        <SectionLabel>Status</SectionLabel>
        <Row label="State">
          <span className={`font-mono font-semibold ${state.color}`}>{state.label}</span>
        </Row>
        <Row label="Geofence revision">
          <span className="font-mono">{event.geofence_revision}</span>
        </Row>
        <Row label="Pending / armed">
          <span className="font-mono">
            {event.enrollment_counts.pending}{' '}
            <span className="text-fg-secondary">/</span>{' '}
            <span className="text-status-online">{event.enrollment_counts.armed}</span>
          </span>
        </Row>
      </CardContent>
    </Card>
  );
};

const ShareCard: React.FC<{
  event: EventRow | null;
  onOpenQr: () => void;
  onCopy: (url: string) => void;
  copyToast: boolean;
}> = ({ event, onOpenQr, onCopy, copyToast }) => {
  const url = event ? publicViewerUrl(event.join_code) : 'tuvion.tv/join/—';
  const disabled = !event?.join_code;

  return (
    <Card className="p-4">
      <CardContent className="space-y-2 p-0">
        <SectionLabel>Device invite link</SectionLabel>
        <p className="text-xs text-fg-secondary">
          Drivers scan this from their phone to join the event roster.
        </p>
        <div className="flex items-center gap-2 rounded-md border border-border bg-bg-input px-2 py-1.5 font-mono text-xs">
          <LinkIcon className="size-4 shrink-0 text-fg-secondary" />
          <span className="flex-1 truncate">{url}</span>
          <button
            type="button"
            aria-label="Copy URL"
            onClick={() => onCopy(url)}
            disabled={disabled}
            className="rounded p-1 text-fg-secondary hover:bg-bg-elevated disabled:opacity-40"
          >
            <Copy className="size-4" />
          </button>
        </div>
        {copyToast && <p className="text-xs text-status-online">Copied to clipboard.</p>}
        <div className="flex gap-2 pt-1">
          <Button
            variant="default"
            size="sm"
            onClick={onOpenQr}
            disabled={disabled}
            className="gap-1"
          >
            <QrCodeIcon className="size-4" />
            Share event
          </Button>
          <Button variant="ghost" size="sm" disabled className="gap-1" title="Coming in a follow-up step">
            <Share2 className="size-4" />
            Social card
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Public spectator URL — separate from the device invite. Only rendered for
 * public events, since private events have no public viewer.
 */
const PublicViewerCard: React.FC<{
  event: EventRow | null;
  onCopy: (url: string) => void;
}> = ({ event, onCopy }) => {
  if (!event || !event.public) return null;
  const url = publicViewerEventUrl(event.id);
  if (!url) return null;

  return (
    <Card className="p-4">
      <CardContent className="space-y-2 p-0">
        <SectionLabel>Public viewer link</SectionLabel>
        <p className="text-xs text-fg-secondary">
          Public spectator URL. Share with fans to watch live without a sign-in.
        </p>
        <div className="flex items-center gap-2 rounded-md border border-border bg-bg-input px-2 py-1.5 font-mono text-xs">
          <LinkIcon className="size-4 shrink-0 text-fg-secondary" />
          <span className="flex-1 truncate">{url}</span>
          <button
            type="button"
            aria-label="Copy public viewer URL"
            onClick={() => onCopy(url)}
            className="rounded p-1 text-fg-secondary hover:bg-bg-elevated"
          >
            <Copy className="size-4" />
          </button>
        </div>
        <p className="text-[11px] text-fg-muted">
          Anyone with the link can watch — no sign-in. Un-tick “Public
          viewer access” in Details to make the event invite-only.
        </p>
      </CardContent>
    </Card>
  );
};

/**
 * Raceline (official stage centerline) upload + public-viewer visibility.
 *
 * The GeoJSON itself lives in S3 (events/<id>/raceline.geojson); the card
 * surfaces vertex count / length / uploaded-at from event metadata. The
 * "Show on public viewer" toggle PATCHes `show_raceline` immediately —
 * doesn't wait for the Save button — so uploading + sharing is a
 * one-click flow once the file lands.
 */
const RacelineCard: React.FC<{
  event: EventRow | null;
  onUpdated: () => Promise<unknown> | void;
}> = ({ event, onUpdated }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!event) return null;

  const hasRaceline = event.raceline_uploaded_at != null;

  const handleFile = async (file: File | null | undefined) => {
    if (!file || !event) return;
    setError(null);
    setUploading(true);
    try {
      await uploadRaceline(event.id, file);
      await onUpdated();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!event || !hasRaceline) return;
    if (!window.confirm('Remove the raceline for this event?')) return;
    setError(null);
    setDeleting(true);
    try {
      await deleteRaceline(event.id);
      await onUpdated();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleToggle = async (next: boolean) => {
    if (!event) return;
    setError(null);
    setToggling(true);
    try {
      await updateEvent(event.id, { show_raceline: next });
      await onUpdated();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setToggling(false);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    void handleFile(file);
  };

  return (
    <Card className="p-4">
      <CardContent className="space-y-3 p-0">
        <div className="flex items-center gap-2">
          <Route className="size-4 text-accent" />
          <SectionLabel>Raceline</SectionLabel>
        </div>
        <p className="text-xs text-fg-secondary">
          Upload the official stage line (GeoJSON LineString). Used for
          stage-progress %, trail trimming, and cross-trajectory compare.
        </p>

        <label
          htmlFor={`raceline-file-${event.id}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border bg-bg-input px-3 py-4 text-xs text-fg-secondary hover:border-accent hover:text-fg"
        >
          <Upload className="size-4" />
          <span>
            {uploading ? 'Uploading…' : 'Drop .geojson or click to choose'}
          </span>
          <input
            ref={fileInputRef}
            id={`raceline-file-${event.id}`}
            type="file"
            accept=".json,.geojson,application/json,application/geo+json"
            className="sr-only"
            disabled={uploading}
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
        </label>

        {hasRaceline ? (
          <div className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="font-mono">
                {event.raceline_vertex_count?.toLocaleString() ?? '?'} pts ·{' '}
                {event.raceline_length_m != null
                  ? `${(event.raceline_length_m / 1000).toFixed(2)} km`
                  : '? km'}
              </span>
              <button
                type="button"
                onClick={handleRemove}
                disabled={deleting}
                className="rounded p-1 text-fg-secondary hover:bg-bg-input hover:text-status-error disabled:opacity-40"
                aria-label="Remove raceline"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
            <div className="mt-1 text-fg-muted">
              uploaded {formatRelative(event.raceline_uploaded_at)}
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-xs text-fg-muted">
            No raceline uploaded yet.
          </div>
        )}

        <label
          className={`flex cursor-pointer items-center gap-2 text-sm ${
            hasRaceline ? '' : 'opacity-50'
          }`}
        >
          <input
            type="checkbox"
            checked={event.show_raceline}
            disabled={!hasRaceline || toggling}
            onChange={(e) => void handleToggle(e.target.checked)}
            className="size-4 cursor-pointer"
          />
          <span>Show raceline on public viewer</span>
        </label>

        {error && (
          <p className="text-xs text-status-error">{error}</p>
        )}
      </CardContent>
    </Card>
  );
};

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const ageMs = Date.now() - t;
  if (ageMs < 60_000) return 'just now';
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} d ago`;
}

const AdminTeamCard: React.FC = () => {
  return (
    <Card className="p-4">
      <CardContent className="space-y-2 p-0">
        <SectionLabel>Admin team</SectionLabel>
        <p className="text-xs text-fg-secondary">
          Single-admin event (you). Multi-admin invitation lands post-MVP.
        </p>
        <Button variant="ghost" size="sm" disabled className="gap-1">
          <UserPlus className="size-4" />
          Invite admin
        </Button>
      </CardContent>
    </Card>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-secondary">
    {children}
  </h3>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-fg-secondary">{label}</Label>
    {children}
  </div>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2 text-sm">
    <span className="text-fg-secondary">{label}</span>
    <span>{children}</span>
  </div>
);

function formatError(err: unknown): string {
  if (err instanceof FleetApiError) {
    if (typeof err.detail === 'string') return err.detail;
    if (Array.isArray(err.detail)) {
      // Pydantic 422 returns [{loc, msg, ...}]
      return err.detail
        .map((e: any) => `${(e.loc || []).join('.')}: ${e.msg}`)
        .join('; ');
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'request failed';
}
